import BaseController from "niteroi/packages/system/controllers/Base.controller";
import {SharedServices} from "niteroi/services/SharedServices";
import UIComponent from "sap/ui/core/UIComponent";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import {ILoadDataResponse, IVIESResponse} from "niteroi/packages/system/models/interfaces";
import Validation from "niteroi/packages/system/helpers/Validation";
import {InputBase$ChangeEvent} from "sap/m/InputBase";
import XMLView from "sap/ui/core/mvc/XMLView";
import Avatar from "sap/m/Avatar";

export default class Pricing extends BaseController {
    public onInit(): void {
        super.onInit();
        SharedServices.updateTheme(this.getOwnerComponent() as UIComponent);
    }

    public onBeforeRendering() {
        super.onBeforeRendering();
        this.setModel(new JSONModel(this.createAcquisitionModel()), "worksNewAccount");
    }

    protected createAcquisitionModel(): object {
        return {
            company: {
                companyId: "",
                companyName: "",
                registrationNumber: "",
                registrationName: "",
                taxId: "",
                vatId: "",
                address: "",
                city: "",
                country: "DE",
                zipCode: "",
                phone: "",
                email: "",
                website: "",
                logo: "",
                parentId: "",
                createdAt: "",
                updatedAt: "",
                isActive: true,
                locale: window.localStorage.getItem("userLanguage")
            }, user: {
                userId: "",
                firstName: "",
                lastName: "",
                gender: "O",
                birthday: "",
                address: "",
                city: "",
                zipCode: "",
                country: "DE",
                nationality: "",
                email: "",
                emailVerified: false,
                phone: "",
                phoneVerified: false,
                password: "",
                companyId: "",
                createdAt: "",
                updatedAt: "",
                isActive: true,
                locale: window.localStorage.getItem("userLanguage"),
                theme: window.localStorage.getItem("userTheme")
            }
        };
    }

    public onPressStartSubscription(): void {
        (this.byId("worksDialogCreateAccountCustomer") as Dialog).open();
    }

    public onChangeInput(event: InputBase$ChangeEvent): void {
        const source = event.getSource();
        const value = source.getValue();
        const id = source.getId();
        const viewId = this.getView().getId();
        if (!value || value.length <= 3) {
            source.setValueState("Error").setShowValueStateMessage(true);
            return;
        }

        if (id === `${viewId}--pricingCreateAccountCompanyEmail` || id === `${viewId}--pricingCreateAccountUserEmail`){
            if (!Validation.isValidEmail(value)) {
                source.setValueState("Error").setShowValueStateMessage(true);
                return;
            }
            source.setValue(value.replace(/\s+/g, ''));
        }

        if (id === `${viewId}--pricingCreateAccountCompanyVatId`) {
            const viesCheck = this.onChangeInputVAT(value);

            let hasErrors = false;
            viesCheck.then((response) => {
                if (response.valid === false) {
                    source.setValueState("Error").setValueStateText(response.userError).setShowValueStateMessage(true);
                    hasErrors = true;
                    return;
                }

                const model = this.getModel("worksNewAccount");
                if (response.name && response.name !== "---") {
                    model.setProperty("/company/registrationName", response.name);
                }
                model.setProperty("/company/country", response.countryCode);
                source.setValueState("Success").setValueStateText("").setShowValueStateMessage(false);
            });
            if (hasErrors) {
                return;
            }
            source.setValue(value.replace(/\s+/g, ''));
        }

        source.setValueState("None");
    }

    protected async onChangeInputVAT(vatId: string): Promise<IVIESResponse> {
        let verification = this.verifyVatWithVies(vatId);

        if (verification.valid === false) {
            return verification;
        } else {
            const viesModel = new JSONModel({});
            const url = "/api/sales-distribution/business-partners/check-vat-number";
            const body = {country: verification.countryCode, number: verification.vatNumber};
            verification = await viesModel.loadData(url, JSON.stringify(body), true, "POST", true, undefined, this.getRequestHeaders()).then(() => {
                return viesModel.getData();
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            });
        }

        return verification;
    }

    public onPressCreateAccount(): void {
        const model = this.getModel("worksNewAccount");
        this.getView().setBusyIndicatorDelay(0).setBusy(true);

        const companyName = model.getProperty("/company/companyName");
        const registrationName = model.getProperty("/company/registrationName");
        const companyEmail = model.getProperty("/company/email");
        const companyVatId = model.getProperty("/company/vatId");
        const userFirstName = model.getProperty("/user/firstName");
        const userLastName = model.getProperty("/user/lastName");
        const userEmail = model.getProperty("/user/email");
        const userPassword = model.getProperty("/user/password");

        let hasErrors = false;
        [
            companyName,
            registrationName,
            companyEmail,
            companyVatId,
            userFirstName,
            userLastName,
            userEmail,
            userPassword
        ].forEach((value) => {
            if (!value || value.length <= 3) {
                this.showToast(this.getText("pricingCreateAccountError", [`${value}`]));
                hasErrors = true;
            }
        });

        if (hasErrors) {
            this.getView().setBusy(false).setBusyIndicatorDelay(1000);
            return;
        }

        const companyRequest = this.createRequestModel();
        companyRequest.loadData(
            "/api/create",
            model.getJSON(),
            true,
            "POST",
            false,
            undefined,
            this.getRequestHeaders()
        ).then(() => {
            const responseData = companyRequest.getData();
            if (!responseData.error) {
                (this.byId("worksDialogCreateAccountCustomer") as Dialog).close();
                this.setModel(new JSONModel(this.createAcquisitionModel()), "worksNewAccount");
                this.showToast(this.getText("pricingCreateAccountSuccess"));
                const avatar = (((this.getOwnerComponent() as UIComponent)?.getRootControl() as XMLView)?.byId("worksShellBarAvatar") as Avatar);
                avatar.firePress();
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false).setBusyIndicatorDelay(1000);
        });
    }
}
